import { Link } from 'react-router-dom';
import { Target, Users, ArrowLeft } from 'lucide-react';
import { Example } from '../types/content';
import { audienceLabels, goalLabels } from '../lib/router';

interface RelatedExamplesProps {
  currentExample: Example;
  allExamples: Example[];
}

export function RelatedExamples({ currentExample, allExamples }: RelatedExamplesProps) {
  // Filter out the current example and find related ones
  const otherExamples = allExamples.filter(ex => ex.slug !== currentExample.slug);
  
  // Score examples based on relatedness
  const scoredExamples = otherExamples.map(example => {
    let score = 0;
    
    // Same complexity level (highest priority)
    if (example.complexity === currentExample.complexity) score += 5;
    
    // Same audience (high priority)
    if (example.audience === currentExample.audience) score += 4;
    
    // Same goal (medium priority)
    if (example.goal === currentExample.goal) score += 3;
    
    // Shared tags (medium priority)
    const sharedTags = example.tags.filter(tag => currentExample.tags.includes(tag));
    score += sharedTags.length * 2;
    
    // Similar keywords in title or context (low priority)
    const currentWords = (currentExample.title + ' ' + currentExample.context).toLowerCase();
    const exampleWords = (example.title + ' ' + example.context).toLowerCase();
    const keywords = ['teaching', 'learning', 'student', 'research', 'analysis', 'plan', 'guide', 'help'];
    const sharedKeywords = keywords.filter(keyword => 
      currentWords.includes(keyword) && exampleWords.includes(keyword)
    );
    score += sharedKeywords.length;
    
    return { example, score };
  });
  
  // Sort by score and take top 4
  const relatedExamples = scoredExamples
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map(item => item.example);
  
  if (relatedExamples.length === 0) {
    return null;
  }
  
  return (
    <div className="hidden lg:block sticky top-[5rem]">
      <div className="sidebar-card">
      <h2 className="sidebar-header">
        <div className="sidebar-header-accent"></div>
        Related Examples
      </h2>
      
      <div className="mb-4 pb-4 border-b border-primary/20">
        <Link to="/examples" className="sidebar-back-btn text-xs">
          <ArrowLeft className="mr-1.5 h-3 w-3" />
          <span>All Examples</span>
        </Link>
      </div>
      
      <div className="space-y-4">
        {relatedExamples.map((example) => (
          <Link
            key={example.slug}
            to={`/examples/${example.slug}`}
            className="block group link-underline"
          >
            <article className="relative pl-5 pb-1 hover:translate-x-1 transition-all duration-200">
              <div className="absolute left-0 top-1 w-1.5 h-1.5 bg-secondary rounded-full group-hover:bg-primary transition-colors"></div>
              <div className="absolute left-0.5 top-2.5 w-0.5 h-full bg-gradient-to-b from-secondary/30 to-transparent"></div>
              
              <h3 className="text-xs font-semibold leading-tight mb-2 line-clamp-2 text-foreground">
                {example.title}
              </h3>
              
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-[10px]">
                  <Target className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">{goalLabels[example.goal]}</span>
                  <span className="text-muted-foreground">•</span>
                  <Users className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">{audienceLabels[example.audience]}</span>
                </div>
                
                {example.tags.length > 0 && (
                  <div>
                    {example.tags.slice(0, 2).map((tag, index) => (
                      <span key={tag}>
                        <Link
                          to={`/tag/${tag}`}
                          className="text-[10px] text-secondary link-underline hover:text-primary transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {tag.replace('-', ' ')}
                        </Link>
                        {index < Math.min(example.tags.length, 2) - 1 && (
                          <span className="text-muted-foreground mx-0.5">|</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          </Link>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-primary/20">
        <Link to="/examples" className="sidebar-back-btn text-xs">
          <ArrowLeft className="mr-1.5 h-3 w-3" />
          <span>All Examples</span>
        </Link>
      </div>
      </div>
    </div>
  );
}